export type DesktopPlatform = "mac" | "other";

export type PlatformShortcutKey = "mod" | "shift";

/** `"mod"` and `"shift"` resolve per platform. Other keys render as written. */
export type ShortcutKey = PlatformShortcutKey | (string & {});

export type ShortcutKeyLabel = {
  label: string;
  ariaLabel: string;
};

const PLATFORM_KEY_LABELS: Record<
  DesktopPlatform,
  Record<PlatformShortcutKey, ShortcutKeyLabel>
> = {
  mac: {
    mod: { label: "⌘", ariaLabel: "Command" },
    shift: { label: "⇧", ariaLabel: "Shift" },
  },
  other: {
    mod: { label: "Ctrl", ariaLabel: "Control" },
    shift: { label: "Shift", ariaLabel: "Shift" },
  },
};

/** Only keys a screen reader should say differently from the chip label. */
const LITERAL_ARIA_LABELS: Record<string, string> = {
  Esc: "Escape",
};

export const detectDesktopPlatform = (): DesktopPlatform => {
  if (typeof navigator === "undefined") return "other";

  const descriptor = `${navigator.platform ?? ""} ${navigator.userAgent ?? ""}`;
  return /mac|iphone|ipad|ipod/i.test(descriptor) ? "mac" : "other";
};

export const getShortcutKeyLabels = (
  platform: DesktopPlatform = detectDesktopPlatform(),
): Record<PlatformShortcutKey, ShortcutKeyLabel> =>
  PLATFORM_KEY_LABELS[platform];

export const resolveShortcutKeys = (
  keys: readonly ShortcutKey[],
  platform: DesktopPlatform = detectDesktopPlatform(),
): ShortcutKeyLabel[] => {
  const platformLabels = getShortcutKeyLabels(platform);

  return keys.map((key) => {
    if (key === "mod") return platformLabels.mod;
    if (key === "shift") return platformLabels.shift;

    return {
      label: key,
      ariaLabel: LITERAL_ARIA_LABELS[key] ?? key,
    };
  });
};

export const joinShortcutAriaLabels = (
  resolved: readonly ShortcutKeyLabel[],
): string => resolved.map((key) => key.ariaLabel).join(" ");

export const formatShortcutAriaLabel = (
  keys: readonly ShortcutKey[],
  platform?: DesktopPlatform,
): string => joinShortcutAriaLabels(resolveShortcutKeys(keys, platform));
